import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { EfileSubmissionManifestTableService } from './efile-submission-manifest-table.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  sanitizeActionParams,
} from '../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-submission-manifest-table',
    templateUrl: './efile-submission-manifest-table.component.html',
    styleUrls: ['./efile-submission-manifest-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileSubmissionManifestTableComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subs = new Subscription();

  // Standard component properties
  modalTitle: string = '';
  minimise: boolean = false;
  isSaveClicked: boolean = false;
  crudLoading: boolean = false;
  saving: boolean = false;
  deleting: boolean = false;
  showFormLoader: boolean = true;

  // Component data properties
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  SERVER_CONFIG: any = {};

  // Form fields
  mode: string = 'create'; // 'create' or 'edit'
  selectedTaxYear: string = '';
  selectedSubId: any = null;
  submissions: any[] = [];
  submissionId: string = '';
  elementName: string = '';
  elementValue: string = '';
  orderId: string = '';
  readOnly: boolean = false;
  showAppliedJurisRequiredError: boolean = false;
  showSubmissionIdRequiredError: boolean = false;
  showElementValueRequiredError: boolean = false;
  showOrderIdRequiredError: boolean = false;

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };

  // Initialization flags
  private isInitialized: boolean = false;
  private isLoading: boolean = false;

  constructor(
    private efileSubmissionManifestTableService: EfileSubmissionManifestTableService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  // Gate function to prevent duplicate initialization
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
      return;
    }

    // Check if we have filterParams for tax_year
    if (!this.filterParams || !this.filterParams.tax_year) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.mode = this.rowData && Object.keys(this.rowData).length > 0 ? 'edit' : 'create';
      this.modalTitle = this.mode === 'create' ? 'Create Entry' : 'Edit Entry';
      this.selectedTaxYear = this.filterParams.tax_year;
      this.loadSubmissions();
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  loadSubmissions(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.showFormLoader = true;

    const params = {
      'action_code': 'pfnojm',
      'sso_id': this.USER_SETTINGS?.user?.sso_id || '',
      'tax_year': this.selectedTaxYear
    };

    this.selectedSubId = null;
    this.submissions = [];

    this.efileSubmissionManifestTableService.loadSubmissions(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess !== "1") {
          this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error loading submissions', 'gtw-snackbar--red');
        } else {
          this.submissions = data.jsonObject || [];
          
          if (this.mode === 'edit' && this.rowData) {
            this.readOnly = true;
            this.submissionId = this.rowData.SUBMISSION_ID;
            this.elementName = this.rowData.ELEMENT_NAME || '';
            this.elementValue = this.rowData.ELEMENT_VALUE || '';
            this.orderId = this.rowData.ORDER_ID || '';
            this.selectedSubId = this.rowData.SUBMISSION_ID;
          }
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading submissions:', error);
        this.gtwSnackbarService.openSnackBar('Something went wrong while loading submission manifest table!', 'gtw-snackbar--red');
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private prepareActionCall(mode: string): any {
    const CREATE_ACTION_CODE = 'A';
    const UPDATE_ACTION_CODE = 'E';
    const DELETE_ACTION_CODE = 'D';
    let actionCode: string;
    
    if (mode === 'create') {
      actionCode = CREATE_ACTION_CODE;
    } else if (mode === 'edit') {
      actionCode = UPDATE_ACTION_CODE;
    } else {
      actionCode = DELETE_ACTION_CODE;
    }

    const url = this.GENERAL_CONFIG.base_url + '/saveJsonObject?action_code=jr60g0';

    const clobSettings = [{
      tax_year: this.filterParams.tax_year || this.globalService?.tax_year,
      scenario: this.filterParams.scenario || this.globalService?.scenario,
      jcd_key: this.globalService?.jcd_key || this.filterParams?.jcd_key
    }];

    if (this.mode === 'edit') {
      this.submissionId = this.rowData.SUBMISSION_ID;
    }

    const clobData: any = {
      'submission_id': this.submissionId ? this.rowData.SUBMISSION_ID : (this.selectedSubId?.SUBMISSION_ID || this.selectedSubId)
    };

    // For delete mode, use rowData values in edit mode (original values from database)
    // This ensures we delete the correct record even if form fields were modified but not saved
    if (mode === 'delete') {
      if (this.mode === 'edit') {
        // In edit mode, always use rowData (original values) for delete
        clobData['element_name'] = this.rowData?.ELEMENT_NAME || '';
        clobData['element_value'] = this.rowData?.ELEMENT_VALUE || '';
        clobData['order_id'] = this.rowData?.ORDER_ID || '';
      } else {
        // In create mode, use current field values
        clobData['element_name'] = this.elementName || '';
        clobData['element_value'] = this.elementValue || '';
        clobData['order_id'] = this.orderId || '';
      }
    } else {
      // For create/edit modes: conditionally add properties only if they have values (matching AngularJS ternary pattern)
      // AngularJS: 'element_name': vm.elementName ? vm.elementName : rowData.ELEMENT_NAME
      const elementNameValue = this.elementName ? this.elementName : (this.mode === 'edit' ? this.rowData?.ELEMENT_NAME : '');
      if (elementNameValue !== null && elementNameValue !== undefined && elementNameValue !== '') {
        clobData['element_name'] = elementNameValue;
      }

      // AngularJS: 'element_value': vm.elementValue ? vm.elementValue : rowData.ELEMENT_VALUE
      const elementValueValue = this.elementValue ? this.elementValue : (this.mode === 'edit' ? this.rowData?.ELEMENT_VALUE : '');
      if (elementValueValue !== null && elementValueValue !== undefined && elementValueValue !== '') {
        clobData['element_value'] = elementValueValue;
      }

      // AngularJS: 'order_id': vm.orderId (no fallback)
      if (this.orderId !== null && this.orderId !== undefined && this.orderId !== '') {
        clobData['order_id'] = this.orderId;
      }
    }

    // Build params following AngularJS pattern
    // Merge globalService.globalParams with filterParams (if globalService has globalParams)
    let params: any = {};
    if (this.globalService && this.globalService) {
      params = _.merge({}, this.globalService, this.filterParams || {});
    } else {
      params = _.merge({}, this.globalService || {}, this.filterParams || {});
    }
    
    // Extend with clob_data and clob_settings (following AngularJS pattern)
    params = _.extend({ 'clob_data': JSON.stringify(clobData) }, params);
    params = _.extend({ 'clob_settings': JSON.stringify(clobSettings) }, params);
    params.sso_id = this.USER_SETTINGS?.user?.sso_id || '';
    params.mode = actionCode;
    params.scenarioCode = this.filterParams.scenarioCode || this.globalService.scenarioCode || 'RAF';
    params.is_issue_key_enabled = this.filterParams.is_issue_key_enabled || this.globalService.is_issue_key_enabled || 'N';
    params.checkLockedbj = JSON.stringify([{ scenario_key: this.filterParams.scenario || this.globalService.scenario || 74 }]);

    return { url: url, params: sanitizeActionParams(params) };
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--red');
      return;
    }

    // Validation
    if (!this.submissions || this.submissions.length === 0) {
      this.gtwSnackbarService.openSnackBar('No submissions available', 'gtw-snackbar--red');
      return;
    }

    // Validation for create mode only - check all fields except order_id
    if (this.mode === 'create') {
      let hasValidationError = false;

      // Check Submission Id
      if (!this.selectedSubId) {
        this.showSubmissionIdRequiredError = true;
        hasValidationError = true;
      } else {
        this.showSubmissionIdRequiredError = false;
      }

      // Check Element Name
      if (!this.elementName || this.elementName.trim() === '') {
        this.showAppliedJurisRequiredError = true;
        hasValidationError = true;
      } else {
        this.showAppliedJurisRequiredError = false;
      }

      // Check Element Value
      if (!this.elementValue || this.elementValue.trim() === '') {
        this.showElementValueRequiredError = true;
        hasValidationError = true;
      } else {
        this.showElementValueRequiredError = false;
      }

      // Check Order Id
      const orderIdStr = this.orderId ? String(this.orderId).trim() : '';
      if (!orderIdStr) {
        this.showOrderIdRequiredError = true;
        hasValidationError = true;
      } else {
        this.showOrderIdRequiredError = false;
      }

      if (hasValidationError) {
        this.gtwSnackbarService.openSnackBar('Please fill all required fields', 'gtw-snackbar--red');
        this.cdr.detectChanges();
        return;
      }
    } else {
      // Edit mode validation - Element Name and Element Value are required
      let hasValidationError = false;

      // Check Element Name
      if (!this.elementName || this.elementName.trim() === '') {
        this.showAppliedJurisRequiredError = true;
        hasValidationError = true;
      } else {
        this.showAppliedJurisRequiredError = false;
      }

      // Check Element Value
      if (!this.elementValue || this.elementValue.trim() === '') {
        this.showElementValueRequiredError = true;
        hasValidationError = true;
      } else {
        this.showElementValueRequiredError = false;
      }

      // Check Order Id
      const orderIdStr = this.orderId ? String(this.orderId).trim() : '';
      if (!orderIdStr) {
        this.showOrderIdRequiredError = true;
        hasValidationError = true;
      } else {
        this.showOrderIdRequiredError = false;
      }

      if (hasValidationError) {
        this.gtwSnackbarService.openSnackBar('Please fill all required fields', 'gtw-snackbar--red');
        this.cdr.detectChanges();
        return;
      }
    }

    const urlParamsObj = this.prepareActionCall(this.mode);
    this.crudLoading = true;
    this.saving = true;
    this.isSaveClicked = true;
    this.cdr.detectChanges();

    // Use service for proper parameter handling
    this.efileSubmissionManifestTableService.saveSubmissionManifestTableData(
      urlParamsObj.url,
      urlParamsObj.params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.saving = false;
          this.crudLoading = false;
          this.isSaveClicked = false;

          if (data.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error saving data', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error saving data:', error);
          this.saving = false;
          this.crudLoading = false;
          this.isSaveClicked = false;
          this.gtwSnackbarService.openSnackBar('Something went wrong while saving data, please try again!', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  delete(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we process this request', 'gtw-snackbar--red');
      return;
    }

    // Validation
    if (!this.submissions || this.submissions.length === 0) {
      this.gtwSnackbarService.openSnackBar('No submissions available', 'gtw-snackbar--red');
      return;
    }

    const elementNameStr = this.elementName ? String(this.elementName).trim() : '';
    if (!elementNameStr) {
      this.showAppliedJurisRequiredError = true;
      this.gtwSnackbarService.openSnackBar('Element Name is required', 'gtw-snackbar--red');
      this.cdr.detectChanges();
      return;
    }

    this.showAppliedJurisRequiredError = false;
    const urlParamsObj = this.prepareActionCall('delete');
    this.crudLoading = true;
    this.deleting = true;
    this.isSaveClicked = true;
    this.cdr.detectChanges();

    // Use service for proper parameter handling
    this.efileSubmissionManifestTableService.deleteSubmissionManifestTableData(
      urlParamsObj.url,
      urlParamsObj.params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.deleting = false;
          this.crudLoading = false;
          this.isSaveClicked = false;

          if (data.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Data deleted successfully.', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error deleting data', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error deleting data:', error);
          this.deleting = false;
          this.crudLoading = false;
          this.isSaveClicked = false;
          this.gtwSnackbarService.openSnackBar('Something went wrong while deleting the data, please try again!', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  // For ui-select dropdown functionality (if needed for search)
  refreshResults($select: any): void {
    // This method can be used for dynamic loading if needed
    // Currently submissions are loaded once on init
  }
}
